// src/components/dashboard/TicketDetail.jsx
import styles from './TicketDetail.module.css';

/**
 * Formats an ISO date string as "Mon D, YYYY" (e.g. "Jul 21, 2026").
 */
function formatDateTime(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return `${months[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
  } catch {
    return iso;
  }
}

/**
 * Priority indicator with color dot.
 */
function PriorityChip({ priority }) {
  const p = (priority || '').toLowerCase();
  const colorMap = {
    high:   '#D32F2F',
    medium: '#BA7517',
    low:    '#0F6E56',
  };
  const color = colorMap[p] || '#9aa3b2';

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
      <span
        style={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          background: color,
          flexShrink: 0,
          display: 'inline-block',
        }}
      />
      {priority || '—'}
    </span>
  );
}

/**
 * A single metadata field cell.
 */
function Field({ label, children }) {
  return (
    <div className={styles.field}>
      <span className={styles.label}>{label}</span>
      <span className={styles.value}>{children}</span>
    </div>
  );
}

/**
 * Expanded detail panel shown below a ticket row.
 *
 * Layout:
 *   1. Summary band  — full-width, amber-tinted
 *   2. Description band — full-width (only if different from summary)
 *   3. 4-column metadata grid
 *
 * @param {{ ticket: object }} props
 */
export default function TicketDetail({ ticket }) {
  const hasSeparateDescription =
    ticket.description &&
    ticket.description !== ticket.summary &&
    ticket.description !== '—';

  return (
    <div className={styles.panel} role="region" aria-label="Ticket details">

      {/* ── Summary band ── */}
      <div className={styles.summaryBand}>
        <p className={styles.summaryBandLabel}>Summary</p>
        <p className={styles.summaryBandValue}>{ticket.summary || '—'}</p>
      </div>

      {/* ── Description band (only if different from summary) ── */}
      {hasSeparateDescription && (
        <div className={styles.descriptionBand}>
          <p className={styles.descriptionBandLabel}>Description</p>
          <p className={styles.descriptionBandValue}>{ticket.description}</p>
        </div>
      )}

      {/* ── Resolution band (only if resolution is present) ── */}
      {ticket.resolution && ticket.resolution !== '—' && (
        <div className={styles.resolutionBand}>
          <p className={styles.resolutionBandLabel}>Resolution</p>
          <p className={styles.resolutionBandValue}>{ticket.resolution}</p>
        </div>
      )}

      {/* ── 4-column metadata grid ── */}
      <div className={styles.grid}>

        {/* ── Attachments ── */}
        {ticket.attachments && ticket.attachments.length > 0 && (
          <div style={{ gridColumn: '1 / -1', marginBottom: '16px' }}>
            <p className={styles.label} style={{ marginBottom: '8px' }}>Attachments</p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {ticket.attachments.map((att, idx) => (
                <a
                  key={idx}
                  href={att.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                    padding: '6px 12px',
                    background: '#f4f4f5',
                    border: '1px solid #e4e4e7',
                    borderRadius: '6px',
                    fontSize: '13px',
                    color: '#3f3f46',
                    textDecoration: 'none'
                  }}
                >
                  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="7 10 12 15 17 10" />
                    <line x1="12" y1="15" x2="12" y2="3" />
                  </svg>
                  {att.title || att.filename || `Attachment ${idx + 1}`}
                </a>
              ))}
            </div>
          </div>
        )}

        <Field label="Ticket Type">{ticket.ticketType || '—'}</Field>

        <Field label="Priority">
          <PriorityChip priority={ticket.priority} />
        </Field>

        <Field label="Root Cause">{ticket.rootCause || '—'}</Field>

        <Field label="Environment">{ticket.environment || '—'}</Field>

        {ticket.loanId && ticket.loanId !== '—' && (
          <Field label="Loan ID">{ticket.loanId}</Field>
        )}

        <Field label="Assigned To">{ticket.assigneeDev || '—'}</Field>

        <Field label="Country">{ticket.country || '—'}</Field>

        <Field label="Submitted">{formatDateTime(ticket.createdAt)}</Field>

      </div>
    </div>
  );
}
